import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt, EMPTY_STRING, simpleValueValidation } from '@miq/utils';
import { Observable } from 'rxjs';
import { ReportRecord } from '../../../../models/report.model';
import {
  DisplayFnArgumentsInfo,
  DisplayFunctionOptions,
  DisplayFunctionPPT,
  LovValues,
} from '../../../../models/template.model';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../utils/dynamic-data-retrievers';
import { getCompanyFieldsInfo } from '../fields-args.const';
import { CbreLogger } from '@cbre/logger';

export const DOLLAR_SIGN = '$';
export const PERCENT_SIGN = '%';

export enum PropertyType {
  Industrial = 'Industrial',
  Office = 'Office',
  Retail = 'Retail',
}

export const extraCurrencyValues: LovValues[] = [
  { key: 'Swedish Krona', value: 'SEK' },
  { key: 'Norwegian Krone', value: 'NOK' },
];

function mapUsagesField(propertyType: PropertyType, arg: DisplayFnArgumentsInfo): DisplayFnArgumentsInfo {
  if (arg.field != null && arg.field.includes('usages')) {
    const field = arg.field.split('usages/').join(`usages[ref_property_usage_type_desc='${propertyType}'].`);
    arg = { ...arg, field };
  }
  if (arg.path != null && arg.path.includes('usages')) {
    const path = arg.path.split('usages.').join(`usages[ref_property_usage_type_desc='${propertyType}'].`);
    arg = { ...arg, path };
  }
  return arg;
}

export function formatCompanyRoleVals(record: ReportRecord, arg: DisplayFnArgumentsInfo, labelId: number): string {
  arg = {
    ...arg,
    ...getCompanyFieldsInfo(labelId),
  };
  const companyVals = mapAndFormatDynamicValue(record, arg);
  return Array.isArray(companyVals) ? (companyVals || []).join(', ') : companyVals;
}

export function propertyTypeDisplayFnFactory(
  propertyType: PropertyType,
  customDisplayFn?: DisplayFunctionPPT
): DisplayFunctionPPT {
  return (record: ReportRecord, options, arg: DisplayFnArgumentsInfo) => {
    const displayFn = customDisplayFn != null ? customDisplayFn : displayFnWrapper;
    const uomArgument = arg.uomArgument != null ? mapUsagesField(propertyType, arg.uomArgument) : null;
    arg = mapUsagesField(propertyType, arg);
    return displayFn(record, options, { ...arg, uomArgument });
  };
}

export function currencyLov(options: DisplayFunctionOptions): Observable<LovValues[]> {
  const { locale, reportExportViewService } = options;
  const lov$ = reportExportViewService.getLovObservablesData(
    '/lookup/currency/symbols',
    undefined,
    locale
  ) as Observable<LovValues[]>;
  return lov$;
}

export function valueWithUomPrefix(
  record: ReportRecord,
  propertyType: PropertyType,
  argument: DisplayFnArgumentsInfo,
  currencyLOV: LovValues[]
): string | string[] {
  function customFn(rec: ReportRecord, _, arg: DisplayFnArgumentsInfo) {
    return mapAndFormatDynamicValue(rec, arg, currencyLOV);
  }
  const localDisplayFn = propertyTypeDisplayFnFactory(propertyType, customFn);
  const result = localDisplayFn(record, undefined, {
    ...argument,
    uomArgument: { ...argument.uomArgument, isPrefix: true },
  }) as string | string[];
  return result;
}

export function getClearHeight(
  record: ReportRecord,
  options: DisplayFunctionOptions,
  min: DisplayFnArgumentsInfo,
  max: DisplayFnArgumentsInfo
) {
  const industrialDisplayFnWrapper = propertyTypeDisplayFnFactory(PropertyType.Industrial);
  const getFirst = (val: string | string[]) => (Array.isArray(val) ? val[0] : val);
  let minVal = industrialDisplayFnWrapper(record, options, min) as string | string[];
  let maxVal = industrialDisplayFnWrapper(record, options, max) as string | string[];
  minVal = getFirst(minVal);
  maxVal = getFirst(maxVal);
  return minVal === defaultTxt && maxVal === defaultTxt ? defaultTxt : concatValuesBy(' - ', minVal, maxVal);
}

export const RoleDescriptions = {
  Tenant: 'Tenant',
  Lessee: 'Lessee',
  TenantRep: 'Tenant Representative',
  LesseeRep: 'Lessee Representative',
  ListingRep: 'Listing Representative',
};

/**
 * If MAX UOM is null for all records, show no UOM in header. If it is the combination of SFs and null, return SF in header
 * If MAX UOMs for different records are not unique,  do not show UOM in header.
 */
export function getUOMValue(uom: string, uomValue: string): string {
  if (uom !== '' && uomValue) {
    if (uom === null) {
      return uomValue;
    }
    if (uom !== uomValue) {
      return '';
    }
  }
  return uom;
}

/**
 * Format Company/Contact records into string using company name and contact
 * display name.
 *
 * @param record
 * @param attributeInfo
 * @param requiredRole
 *
 * @example
 * Expected output from this method for a single tim record
 * CompanyName1 - Contact1,Contact2,Contact3
 * CompanyName2 - Contact2,Contact3,Contact4
 */
export function formatCompanyContactByRole(
  record: ReportRecord,
  attributeInfo: DisplayFnArgumentsInfo,
  requiredRole: string,
  separator: string,
  logger?: CbreLogger
): string {
  if (!simpleValueValidation(attributeInfo.field)) {
    logger?.warn(`Missing field value for field: ${attributeInfo.id}`);
    return defaultTxt;
  }
  const companyContactRecords = digger(record.data[0], 'usages/company_contact_role_addresses');
  const uniqueCompanyIds = [...new Set(companyContactRecords.map(item => item.company_id))];
  const formattedCompanyContacts: string[] = uniqueCompanyIds.map((companyId, index) => {
    // filter company/contact record by id and role
    const filteredCompanyContacts = companyContactRecords.filter(
      itemContact => itemContact.company_id === companyId && itemContact.role_desc === requiredRole
    );
    let companyName: string;
    if (filteredCompanyContacts.length) {
      companyName = filteredCompanyContacts[0].company_name;
      // join contact display names
      const contactsDisplayName = filteredCompanyContacts
        .map(itemContact => itemContact.contact_display_name)
        .join(', ');
      return companyName ? `${companyName} ${separator} ${contactsDisplayName}` : EMPTY_STRING;
    }
    return EMPTY_STRING;
  });
  return formattedCompanyContacts.length > 0
    ? formattedCompanyContacts.filter(data => data != EMPTY_STRING).join('\n')
    : defaultTxt;
}
